<template>
  <div class="remarks-list">
    <div class="content">
      <p class="subject">{{ $t('common.remarks') }}</p>
      <ol class="items">
        <li v-if="isAsIs">
          <div class="default-item">
            <p>
              {{ asIsRemarkTemplate.title }}
              <input
                type="radio"
                v-show="allowEdit"
                value="UNEDITABLE"
                v-model="remarksListAsIsRadio"
                @change="changeRemarksListAsIsRadio"
              >
            </p>
            <pre>{{ asIsRemarkTemplate.content }}</pre>
            <div
              v-if="showAdditionalContent"
              class="addContentPoints"
            >
              <table v-if="$te(`terms.${regionName}.hotb.content.takebackRemarksPointOne`)">
                <tr>
                  <td style="width: 25px;" />
                  <td style="width: 50px;">(i)</td>
                  <td>{{ $t(`terms.${regionName}.hotb.content.takebackRemarksPointOne`) }}</td>
                </tr>
                <tr>
                  <td />
                  <td>(ii)</td>
                  <td>{{ $t(`terms.${regionName}.hotb.content.takebackRemarksPointTwo`) }}</td>
                </tr>
                <tr>
                  <td colspan="3">{{ $t(`terms.${regionName}.hotb.content.takebackRemarksPointDescribe`) }}</td>
                </tr>
                <tr>
                  <td />
                  <td>(iii)</td>
                  <td>{{ $t(`terms.${regionName}.hotb.content.takebackRemarksPointThree`) }}</td>
                </tr>
              </table>
            </div>
          </div>
          <div class="editable-item">
            <p>
              {{ currentRemarksList.title }}
              <input
                v-show="allowEdit"
                type="radio"
                value="EDITABLE"
                v-model="remarksListAsIsRadio"
                @change="changeRemarksListAsIsRadio"
              >
              <i
                v-show="allowEdit && !isEditing"
                class="fa fa-pencil"
                @click="isEditing = true"
              />
            </p>
            <TextField
              :readonly="!isEditing"
              :afterBlur="afterBlur"
              :showColon="false"
              :value="currentRemarksList.content || (isEditing ? '' : $t('remarkNA'))"
            />
          </div>
        </li>
        <li
          v-for="(item, index) in defaultTemplate"
          :key="index"
        >
          <div class="title">{{ item.title }}</div>
          <pre class="content">{{ item.content }}</pre>
        </li>
        <li v-if="isHandover">
          <div class="title">{{ documentsRemarks.title }}</div>
          <pre class="content">{{ documentsRemarks.content }}</pre>
        </li>
        <li>
          <div class="title">{{ currentRemarksList.othersTitle }}</div>
          <div class="default-item-other">
            <p>
              {{ $t('remarkNA') }}
              <input
                type="radio"
                v-show="allowEdit"
                value="UNEDITABLE"
                v-model="otherRemarksRadio"
                @change="changeOtherRemarksRadio"
              >
            </p>
          </div>
          <div class="editable-item-other">
            <p>
              {{ $t('action.edit') }}
              <input
                v-show="allowEdit"
                type="radio"
                value="EDITABLE"
                v-model="otherRemarksRadio"
                @change="changeOtherRemarksRadio"
              >
              <i
                v-show="allowEdit && !isEditingOthers"
                @click="isEditingOthers = true"
                class="fa fa-pencil"
              />
            </p>
            <TextField
              :readonly="!isEditingOthers"
              :readonlyAsInput="!isEditingOthers"
              :afterBlur="event => afterBlur(event, true)"
              :placeholder="$t(`terms.${regionName}.hotb.content.otherRemarksHint`)"
              :showColon="false"
              :value="currentRemarksList.othersContent"
            />
          </div>
        </li>
        <li>
          <div class="title">{{ asIsDefinitions.title }}</div>
          <pre class="content">{{ asIsDefinitions.content }}</pre>
        </li>
      </ol>
    </div>
  </div>
</template>

<script>
import lodash from 'lodash'
import { mapGetters, mapMutations, mapActions } from 'vuex'

import TextField from '@/controls/TextField'

import mutationTypes from '@/store/mutation-types'
import { getSubsidiaryTextArray } from '@/api/postman/util'
import { hotbDefaultRemarksTemplate, hotbEditableTemplate, documentsHandedOver, takebackRemarksAsIsTemplate,
  handoverRemarksAsIsTemplate, asIsDefinitions, HOTB_REMARK_LIST_AS_IS_RADIO } from './Contains'
import { hotbPremiseBuildingType } from '@/utils/locale'
import { utils as formUtils } from '@/plugin/InputChange'

export default {
  name: 'RemarkList',
  components: {
    TextField,
  },
  props: {
    allowEdit: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return (
      {
        isEditing: false,
        isEditingOthers: false,
      }
    )
  },
  computed: {
    ...mapGetters('hotb', ['currentHotb', 'isHandover']),
    ...mapGetters('regions', ['regionName']),
    companyName () {
      const subsidiary = lodash.get(this.currentHotb, ['physicalSite', 'subsidiary'], '')
      const subsidiaryArray = getSubsidiaryTextArray(subsidiary)
      const result = lodash.get(subsidiaryArray, [0], '')
      return result
    },
    defaultTemplate () {
      return hotbDefaultRemarksTemplate(this.isHandover, this.companyName, this.isAsIs, this.regionName)
    },
    isAsIs () {
      return this.currentHotb && lodash.get(this.currentHotb, 'premiseBuildingType', '') === hotbPremiseBuildingType.asIs
    },
    showAdditionalContent () {
      return this.isAsIs && !this.isHandover
    },
    currentRemarksList () {
      const remarksList = lodash.get(this.currentHotb, 'remarksList', {})
      return hotbEditableTemplate(this.isHandover, remarksList, this.regionName)
    },
    asIsRemarkTemplate () {
      return this.isHandover ? handoverRemarksAsIsTemplate(this.regionName) : takebackRemarksAsIsTemplate(this.regionName)
    },
    documentsRemarks () {
      return documentsHandedOver(lodash.get(this.currentHotb, 'docsToTenant', []), this.regionName)
    },
    asIsDefinitions () {
      return asIsDefinitions(this.currentHotb, this.isAsIs, this.regionName)
    },
    remarksListAsIsRadio () {
      return lodash.get(this.currentHotb, 'remarksListAsIsRadio', '')
    },
    otherRemarksRadio () {
      return lodash.get(this.currentHotb, 'otherRemarksRadio', '')
    },
  },
  methods: {
    ...mapMutations('hotb', {
      updateFieldByPath: mutationTypes.HOTB_UPDATE_FIELD_PATH,
    }),
    ...mapActions('hotb', ['updateHotb']),
    updateRemarksList (val, isOthers) {
      let toUpdate
      if (isOthers) {
        this.isEditingOthers = false
        toUpdate = { othersContent: val }
      } else {
        this.isEditing = false
        toUpdate = { content: val }
      }
      this.updateFieldByPath({path: ['remarksList'], newValue: {...this.currentRemarksList, ...toUpdate}})
    },
    combinateRemarksList () {
      let remarksListRequest = []
      if (this.isAsIs) {
        if (this.remarksListAsIsRadio === HOTB_REMARK_LIST_AS_IS_RADIO.EDITABLE) {
          remarksListRequest.push({title: this.currentRemarksList.title, content: this.currentRemarksList.content || this.$t('remarkNA')})
        } else if (this.remarksListAsIsRadio === HOTB_REMARK_LIST_AS_IS_RADIO.UNEDITABLE) {
          remarksListRequest.push(this.isHandover ? handoverRemarksAsIsTemplate(this.regionName) : takebackRemarksAsIsTemplate(this.regionName))
        }
      }
      remarksListRequest = remarksListRequest.concat(this.defaultTemplate)
      if (this.isHandover) {
        remarksListRequest.push(this.documentsRemarks)
      }
      const othersContent = this.otherRemarksRadio === HOTB_REMARK_LIST_AS_IS_RADIO.EDITABLE
        ? this.currentRemarksList.othersContent || '--'
        : this.$t('remarkNA')
      remarksListRequest.push({title: this.currentRemarksList.othersTitle, content: othersContent})
      remarksListRequest.push(this.asIsDefinitions)
      return remarksListRequest
    },
    async afterBlur (e, isOthers) {
      formUtils.resetDirty()
      this.updateRemarksList(e.currentTarget.value.trim(), isOthers)
      await this.updateHotb({attrs: ['remarksList'], notify: false})
    },
    async changeRemarksListAsIsRadio (val) {
      this.updateFieldByPath({path: ['remarksListAsIsRadio'], newValue: val.currentTarget.value})
      await this.updateHotb({attrs: ['remarksListAsIsRadio'], notify: false})
    },
    async changeOtherRemarksRadio (val) {
      this.updateFieldByPath({path: ['otherRemarksRadio'], newValue: val.currentTarget.value})
      await this.updateHotb({attrs: ['otherRemarksRadio'], notify: false})
    },
  },
}
</script>

<style lang="scss">
.remarks-list {
  margin-top: 20px;
  .subject {
    font-size: 16px;
    font-weight: bold;
  }
  .items li {
    margin: 10px 0;
  }
  .default-item {
    margin-bottom: 15px;
  }
  .default-item-other,
  .editable-item-other {
    p {
      margin-bottom: 5px;
    }
  }
  .default-item,
  .editable-item,
  .default-item-other,
  .editable-item-other {
    input[type='radio'] {
      position: relative;
      top: 5px;
      height: 20px;
      width: 20px;
      margin: 0;
    }
    p {
      line-height: 1.43 !important;
    }
    .title {
      line-height: 1.6;
    }
  }
  .fa-pencil {
    cursor: pointer;
  }
  pre {
    overflow: hidden;
  }
  .addContentPoints{
    p {
      margin:0;
    }
    table {
    border-collapse: collapse;
    border: 0;
    }
    td {
    padding: 0;
    vertical-align: text-top;
    }
  }

}
</style>
